/**
 * AddConnectorPopup - Nested dialog form for creating a new data connector.
 *
 * Allows users to select a connector type, provide a name, and enter an API token.
 * Auto-fills the connector name from the selected type's display name.
 * Disables "Create" when any required field is empty/whitespace.
 * On success, triggers list refetch and closes; on failure, shows error and stays open.
 *
 * Uses ConnectorFormProvider with split state/actions contexts and isolated field
 * components to prevent unnecessary re-renders on individual field changes.
 */

import { Box, Input, Text, VStack } from "@chakra-ui/react"
import { Switch } from "@chakra-ui/react"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { getConnectorConfig } from "@hydrologiq/shared/data-connections/constants"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { useConnectorTypes } from "./hooks/useConnectorTypes"
import { useCreateIntegrationSource } from "./hooks/useCreateIntegrationSource"
import { useCreateMockIntegrationSource } from "./hooks/useCreateMockIntegrationSource"
import { buildVaultSecret, canSubmitForm, getAutoFilledName, mapConnectorTypesToDropdownItems } from "./addConnectorValidation"
import { ConnectorFormProvider, useConnectorFormState, useConnectorFormActions } from "./ConnectorFormProvider"

interface AddConnectorPopupProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  /** Platform admins may create a mock connector for testing. */
  isPlatformAdmin?: boolean
}

// --- Field components (each subscribes only to the state it needs) ---

const ConnectorTypeField = ({ creating }: { creating: boolean }) => {
  const { connectorType } = useConnectorFormState()
  const { setConnectorType, setConnectorName, setFixture } = useConnectorFormActions()
  const { connectorTypes, loading: typesLoading, error: typesError } = useConnectorTypes()

  const typeItems = mapConnectorTypesToDropdownItems(connectorTypes, typesLoading)

  const handleTypeChange = (value: string) => {
    setConnectorType(value)
    const autoFilled = getAutoFilledName(connectorTypes, value)
    if (autoFilled) {
      setConnectorName(autoFilled)
    }
    // Reset fixture to the first available option for the selected connector type
    const fixtureOptions = CONNECTOR_FIXTURE_OPTIONS[value.toUpperCase()] ?? DEFAULT_FIXTURE_OPTIONS
    setFixture(fixtureOptions[0].value)
  }

  return (
    <>
      {typesError && (
        <Box bg="#FEF2F2" border="1px solid" borderColor="#F87171" borderRadius="md" p={3}>
          <Text  fontSize="sm">
            Failed to load connector types. Please try again later.
          </Text>
        </Box>
      )}
      <Field label="Connector type">
        <NativeSelectRoot disabled={typesLoading || !!typesError || creating}>
          <NativeSelectField
            value={connectorType}
            onChange={(e) => handleTypeChange(e.target.value)}
            items={typeItems}
          />
        </NativeSelectRoot>
      </Field>
    </>
  )
}

const ConnectorNameField = ({ creating }: { creating: boolean }) => {
  const { connectorName, connectorType } = useConnectorFormState()
  const { setConnectorName } = useConnectorFormActions()

  return (
    <Field label="Connector name">
      <Input
        value={connectorName}
        onChange={(e) => setConnectorName(e.target.value)}
        placeholder="Enter connector name"
        disabled={creating || !connectorType}
      />
    </Field>
  )
}

const ApiTokenField = ({ creating }: { creating: boolean }) => {
  const { apiToken, connectorType } = useConnectorFormState()
  const { setApiToken } = useConnectorFormActions()

  return (
    <Field label="API token">
      <Input
        value={apiToken}
        onChange={(e) => setApiToken(e.target.value)}
        placeholder="Enter API token"
        disabled={creating || !connectorType}
      />
    </Field>
  )
}

/** Renders one input per declared credential field for the selected connector
 *  type. Secret fields use a masked (`password`) input and are never echoed.
 *  Values are written into `credentialValues` via `setCredentialValue`. */
const CredentialFields = ({ creating }: { creating: boolean }) => {
  const { connectorType, credentialValues } = useConnectorFormState()
  const { setCredentialValue } = useConnectorFormActions()

  const fields = getConnectorConfig(connectorType).credentialFields ?? []

  return (
    <>
      {fields.map((field) => (
        <Field key={field.key} label={field.label}>
          <Input
            type={field.secret ? "password" : "text"}
            value={credentialValues[field.key] ?? ""}
            onChange={(e) => setCredentialValue(field.key, e.target.value)}
            placeholder={field.placeholder ?? `Enter ${field.label}`}
            disabled={creating || !connectorType}
            autoComplete={field.secret ? "new-password" : undefined}
          />
        </Field>
      ))}
    </>
  )
}

/** Credential entry section: renders per-field inputs when the selected
 *  connector type declares `credentialFields`, otherwise the single API token
 *  input (backwards compatible). */
const CredentialSection = ({ creating }: { creating: boolean }) => {
  const { connectorType } = useConnectorFormState()
  const hasCredentialFields = !!getConnectorConfig(connectorType).credentialFields?.length

  return hasCredentialFields ? (
    <CredentialFields creating={creating} />
  ) : (
    <ApiTokenField creating={creating} />
  )
}

const MockSourceCheckbox = ({ creating, isPlatformAdmin }: { creating: boolean; isPlatformAdmin: boolean }) => {
  const { useMock } = useConnectorFormState()
  const { setUseMock } = useConnectorFormActions()

  if (!isPlatformAdmin) return null

  return (
    <Box>
      <Switch.Root
        checked={useMock}
        onCheckedChange={(e) => setUseMock(e.checked)}
        disabled={creating}
        size="sm"
      >
        <Switch.HiddenInput />
        <Switch.Control>
          <Switch.Thumb />
        </Switch.Control>
        <Switch.Label>
          <Text fontSize="sm">Create mock source (test data)</Text>
        </Switch.Label>
      </Switch.Root>
    </Box>
  )
}

/** Fixture options per connector type. Connectors with a single entry show
 *  a static "Default" label instead of a dropdown. */
const CONNECTOR_FIXTURE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  VRM: [
    { value: "bsu", label: "BSU (default)" },
    { value: "tank", label: "Tank" },
    { value: "ac", label: "AC Load" },
    { value: "dc", label: "DC Load" },
    { value: "garic-ses", label: "Garic SES" },
    { value: "garic-cabin-combi", label: "Garic Cabin Combi" },
    { value: "garic-cabin-eco", label: "Garic Cabin Eco" },
  ],
  FCSL: [
    { value: "default", label: "Default" },
  ],
  ENAPTER: [
    { value: "default", label: "Default" },
  ],
}

const DEFAULT_FIXTURE_OPTIONS = [{ value: "default", label: "Default" }]

const FixtureField = ({ creating }: { creating: boolean }) => {
  const { useMock, fixture, connectorType } = useConnectorFormState()
  const { setFixture } = useConnectorFormActions()

  if (!useMock) return null

  const options = CONNECTOR_FIXTURE_OPTIONS[connectorType.toUpperCase()] ?? DEFAULT_FIXTURE_OPTIONS

  if (options.length <= 1) {
    return (
      <Field label="Fixture" helperText="This connector has a single test data set">
        <Text fontSize="sm" color="fg.muted">{options[0]?.label ?? "Default"}</Text>
      </Field>
    )
  }

  return (
    <Field label="Fixture" helperText="Select the test data set this mock source will serve">
      <NativeSelectRoot disabled={creating}>
        <NativeSelectField
          value={fixture}
          onChange={(e) => setFixture(e.target.value)}
          items={options}
        />
      </NativeSelectRoot>
    </Field>
  )
}

const ErrorDisplay = () => {
  const { error } = useConnectorFormState()
  if (!error) return null
  return (
    <Box
      bg="#FEF2F2"
      border="1px solid"
      borderColor="#F87171"
      borderRadius="md"
      p={3}
    >
      <Text  fontWeight="semibold" fontSize="sm">
        Failed to create connector
      </Text>
      <Text  fontSize="sm" mt={1}>
        {error}
      </Text>
    </Box>
  )
}

// --- Footer ---

const DialogFooter = ({
  creating,
  onSuccess,
  onClose,
}: {
  creating: boolean
  onSuccess: () => void
  onClose: () => void
}) => {
  const { connectorType, connectorName, apiToken, credentialValues, useMock, fixture } = useConnectorFormState()
  const { setError, reset } = useConnectorFormActions()
  const { createSource } = useCreateIntegrationSource()
  const { createMockSource, loading: creatingMock } = useCreateMockIntegrationSource()

  const credentialFields = getConnectorConfig(connectorType).credentialFields

  const canSubmit = canSubmitForm(connectorType, connectorName, apiToken, credentialFields, credentialValues)

  const handleSubmit = async () => {
    if (!canSubmit) return

    setError(null)
    try {
      // Build the single opaque `vaultSecret` string sent to the backend. When
      // the selected connector type declares `credentialFields`, the collected
      // values are serialized (in declared field order, trimmed) into a JSON
      // object; otherwise it falls back to the single trimmed API token
      // (backwards compatible). Serialization is a pure, tested helper.
      const vaultSecret = buildVaultSecret(credentialFields, credentialValues, apiToken)
      const result = useMock
        ? await createMockSource({
            variables: {
              input: {
                type: connectorType.trim(),
                name: connectorName.trim(),
                vaultSecret,
                config: { fixture },
              },
            },
          })
        : await createSource({
            variables: {
              input: {
                type: connectorType.trim(),
                name: connectorName.trim(),
                vaultSecret,
              },
            },
          })

      if (result.errors && result.errors.length > 0) {
        const rawMessage = result.errors[0].message || ""
        const message = rawMessage.includes("invalid input syntax for type uuid")
          ? "A connector with this name already exists. Please choose a different name."
          : rawMessage || "An unexpected error occurred. Please try again."
        setError(message)
        toaster.error({ title: "Failed to create connector", description: message })
        return
      }

      reset()
      toaster.success({ title: "Connector created successfully" })
      onSuccess()
      onClose()
    } catch (err: any) {
      const rawMessage = err?.message || ""
      const message = rawMessage.includes("invalid input syntax for type uuid")
        ? "A connector with this name already exists. Please choose a different name."
        : rawMessage || "An unexpected error occurred. Please try again."
      setError(message)
      toaster.error({ title: "Failed to create connector", description: message })
    }
  }

  return (
    <ActionButton
      disabled={!canSubmit || creating || creatingMock}
      onClick={handleSubmit}
      context={{
        label: "Create",
        description: "Create the data connector",
        variant: "primary",
      }}
    />
  )
}

// --- Inner dialog ---

function AddConnectorPopupInner({ open, onClose, onSuccess, isPlatformAdmin = false }: AddConnectorPopupProps) {
  const { reset } = useConnectorFormActions()
  const { loading: creating } = useCreateIntegrationSource()

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog
      title="Add new connector"
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) handleClose()
      }}
      onClose={handleClose}
      footer={
        <DialogFooter creating={creating} onSuccess={onSuccess} onClose={handleClose} />
      }
    >
      <VStack gap={4} width="100%" align="stretch">
        <ErrorDisplay />
        <ConnectorTypeField creating={creating} />
        <ConnectorNameField creating={creating} />
        <CredentialSection creating={creating} />
        <MockSourceCheckbox creating={creating} isPlatformAdmin={isPlatformAdmin} />
        <FixtureField creating={creating} />
      </VStack>
    </Dialog>
  )
}

// --- Exported component ---

export function AddConnectorPopup({ open, onClose, onSuccess, isPlatformAdmin }: AddConnectorPopupProps) {
  return (
    <ConnectorFormProvider>
      <AddConnectorPopupInner open={open} onClose={onClose} onSuccess={onSuccess} isPlatformAdmin={isPlatformAdmin} />
    </ConnectorFormProvider>
  )
}

export default AddConnectorPopup
